/**
 * L3 Semantic Audit — stranded spokes + low-value definitions + topology_orphans_with_no_demand.
 *
 * Uwaga: L3 NIE czyta thematic_node_content.fan_out. PAA baseline lezy
 * w thematic_nodes.paa_preview (map-time, fetched przez paaBaselineFetcher
 * PRZED audytem). CE fan_out zostawiamy osobno.
 *
 * 3 twarde reguły (user spec):
 *  1. stranded_spokes: role ∈ (comparison, troubleshooting, guide)
 *     + SV=0/null/tier=ND + paa_count=0 + no_match + no_incoming
 *  2. low_value_definitions: role=definition + SV<50 + paa_count=0 + no_match
 *  3. topology_orphans_with_no_demand: incoming=0 + role≠pillar + (SV=0 OR ND)
 */

import { supabase } from "@/integrations/supabase/client";
import type { AuditConfig, AuditSeverity, AuditIssue } from "@/types/thematicMaps";

const PREFIX = "[AUDIT-L3-SEM]";

export interface L3Result {
  score: number;
  max: number;
  verdict: "pass" | "warn" | "fail";
  stranded_spokes: {
    count: number;
    ratio: number;
    severity: AuditSeverity;
    affected: Array<{ slug: string; document_role: string; search_volume: number; seed_name: string }>;
  };
  low_value_definitions: {
    count: number;
    affected: Array<{ slug: string; search_volume: number; seed_name: string }>;
  };
  topology_orphans_with_no_demand: {
    count: number;
    affected: Array<{ slug: string; document_role: string; seed_name: string }>;
  };
  demand_coverage_summary: {
    nodes_with_sv: number;
    nodes_with_paa: number;
    nodes_with_both: number;
    nodes_with_neither: number;
  };
  issues: AuditIssue[];
}

interface SemanticNode {
  id: string;
  slug: string;
  document_role: string;
  node_role: string | null;
  seed_id: string;
  search_volume: number | null;
  demand_tier: string | null;
  matched_wp_post_id: string | null;
  paa_preview: unknown;
}

interface RelationRow {
  from_node_id: string;
  to_node_id: string;
}

const EVALUATIVE_ROLES = new Set(["comparison", "troubleshooting", "guide"]);

const DEFAULT_THRESHOLDS = {
  stranded_ratio: { error: 30, warning: 15 },
  max_affected_per_issue: 10,
};

function paaCount(node: SemanticNode): number {
  return Array.isArray(node.paa_preview) ? node.paa_preview.length : 0;
}

function noDemand(n: SemanticNode): boolean {
  return (n.search_volume ?? 0) === 0 || n.demand_tier === "ND";
}

export async function auditSemantic(
  mapId: string,
  config?: AuditConfig,
): Promise<L3Result> {
  const maxAffected = config?.max_affected_per_issue ?? DEFAULT_THRESHOLDS.max_affected_per_issue;
  const strandedCfg = config?.stranded_ratio ?? DEFAULT_THRESHOLDS.stranded_ratio;

  // Load nodes with PAA + SRO + matching fields
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data: nodesData } = await (supabase as any)
    .from("thematic_nodes")
    .select(
      "id, slug, document_role, node_role, seed_id, search_volume, demand_tier, matched_wp_post_id, paa_preview",
    )
    .eq("map_id", mapId);
  const nodes = (nodesData || []) as SemanticNode[];

  // Seeds — do mapowania seed_id → seed_name
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data: seedsData } = await (supabase as any)
    .from("thematic_seeds")
    .select("id, name")
    .eq("map_id", mapId);
  const seedNameMap = new Map<string, string>(
    ((seedsData || []) as Array<{ id: string; name: string }>).map((s) => [s.id, s.name]),
  );

  // Incoming edges (exclude parent relation — kazda relacja thematic_node_relations liczy)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data: relData } = await (supabase as any)
    .from("thematic_node_relations")
    .select("from_node_id, to_node_id")
    .eq("map_id", mapId);
  const relations = (relData || []) as RelationRow[];

  const incoming = new Map<string, number>();
  for (const n of nodes) incoming.set(n.id, 0);
  for (const r of relations) {
    incoming.set(r.to_node_id, (incoming.get(r.to_node_id) ?? 0) + 1);
  }

  const issues: AuditIssue[] = [];

  // ─── Reguła 1: stranded_spokes ───
  const evaluativeSpokes = nodes.filter((n) => EVALUATIVE_ROLES.has(n.document_role));
  const stranded = evaluativeSpokes.filter(
    (n) =>
      noDemand(n) &&
      paaCount(n) === 0 &&
      !n.matched_wp_post_id &&
      (incoming.get(n.id) ?? 0) === 0,
  );
  const strandedRatio = evaluativeSpokes.length > 0
    ? (stranded.length / evaluativeSpokes.length) * 100
    : 0;
  const strandedSeverity: AuditSeverity =
    strandedRatio >= strandedCfg.error ? "error"
    : strandedRatio >= strandedCfg.warning ? "warning" : "ok";

  const strandedAffected = stranded.slice(0, maxAffected).map((n) => ({
    slug: n.slug,
    document_role: n.document_role,
    search_volume: n.search_volume ?? 0,
    seed_name: seedNameMap.get(n.seed_id) ?? "—",
  }));

  if (strandedSeverity !== "ok") {
    issues.push({
      severity: strandedSeverity,
      dimension: "semantic",
      message: `${stranded.length}/${evaluativeSpokes.length} evaluative spokes stranded (${strandedRatio.toFixed(1)}%): role+SV=0+PAA=0+noMatch+noIncoming`,
      affected_slugs: strandedAffected.map((a) => a.slug),
      suggested_fix: "Zweryfikuj ręcznie czy te nody mają sens. Rozważ: (a) dodaj demand signal (nowe keyword), (b) usuń node, (c) zmapuj do istniejącego wpisu WP",
    });
  }

  // ─── Reguła 2: low_value_definitions ───
  const lowValueDefs = nodes.filter(
    (n) =>
      n.document_role === "definition" &&
      (n.search_volume ?? 0) < 50 &&
      paaCount(n) === 0 &&
      !n.matched_wp_post_id,
  );
  const lowValueAffected = lowValueDefs.slice(0, maxAffected).map((n) => ({
    slug: n.slug,
    search_volume: n.search_volume ?? 0,
    seed_name: seedNameMap.get(n.seed_id) ?? "—",
  }));

  if (lowValueDefs.length > 0) {
    issues.push({
      severity: lowValueDefs.length > 5 ? "warning" : "info",
      dimension: "semantic",
      message: `${lowValueDefs.length} definicji low-value (SV<50 + PAA=0 + noMatch) — kandydaci do wykasowania z mapy`,
      affected_slugs: lowValueAffected.map((a) => a.slug),
      suggested_fix: "Przejrzyj listę i usuń definicje bez demand signal (chyba że entity anchor)",
    });
  }

  // ─── Reguła 3: topology_orphans_with_no_demand ───
  const topoOrphansNoDemand = nodes.filter(
    (n) =>
      (incoming.get(n.id) ?? 0) === 0 &&
      n.node_role !== "pillar" &&
      noDemand(n),
  );
  const topoAffected = topoOrphansNoDemand.slice(0, maxAffected).map((n) => ({
    slug: n.slug,
    document_role: n.document_role,
    seed_name: seedNameMap.get(n.seed_id) ?? "—",
  }));

  if (topoOrphansNoDemand.length > 0) {
    issues.push({
      severity: topoOrphansNoDemand.length > 10 ? "warning" : "info",
      dimension: "semantic",
      message: `${topoOrphansNoDemand.length} topology orphans bez demand — izolowane + bez SV`,
      affected_slugs: topoAffected.map((a) => a.slug),
      suggested_fix: "Dodaj relacje wchodzące z powiązanych seedów (sibling/next_step/glossary_ref) lub usuń nody",
    });
  }

  // ─── Demand coverage summary ───
  const nodesWithSv = nodes.filter((n) => (n.search_volume ?? 0) > 0).length;
  const nodesWithPaa = nodes.filter((n) => paaCount(n) > 0).length;
  const nodesWithBoth = nodes.filter((n) => (n.search_volume ?? 0) > 0 && paaCount(n) > 0).length;
  const nodesWithNeither = nodes.filter((n) => (n.search_volume ?? 0) === 0 && paaCount(n) === 0).length;

  // ─── Score (0-20) ───
  let score = 20;
  if (strandedSeverity === "error") score -= 8;
  else if (strandedSeverity === "warning") score -= 4;
  if (lowValueDefs.length > 10) score -= 4;
  else if (lowValueDefs.length > 5) score -= 2;
  if (topoOrphansNoDemand.length > 15) score -= 3;
  else if (topoOrphansNoDemand.length > 5) score -= 1;
  if (nodes.length > 0 && nodesWithNeither / nodes.length > 0.7) score -= 3; // catastrophic demand coverage
  score = Math.max(0, score);

  const verdict: "pass" | "warn" | "fail" = score >= 16 ? "pass" : score >= 10 ? "warn" : "fail";

  return {
    score,
    max: 20,
    verdict,
    stranded_spokes: {
      count: stranded.length,
      ratio: Math.round(strandedRatio * 10) / 10,
      severity: strandedSeverity,
      affected: strandedAffected,
    },
    low_value_definitions: {
      count: lowValueDefs.length,
      affected: lowValueAffected,
    },
    topology_orphans_with_no_demand: {
      count: topoOrphansNoDemand.length,
      affected: topoAffected,
    },
    demand_coverage_summary: {
      nodes_with_sv: nodesWithSv,
      nodes_with_paa: nodesWithPaa,
      nodes_with_both: nodesWithBoth,
      nodes_with_neither: nodesWithNeither,
    },
    issues,
  };
}

export { PREFIX as L3_PREFIX };
